import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Info, Minus, Plus, Check, Loader2 } from 'lucide-react';
import { usePolarCheckout } from '@/hooks/usePolarCheckout';
import { POLAR_PRODUCTS } from '@/config/polar';
import { useAuth } from '@/hooks/useAuth';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle } from
'@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider } from
'@/components/ui/tooltip';

type BillingCycle = 'monthly' | 'yearly';

interface PricingTier {
  min: number;
  max: number;
  monthlyPrice: number;
  yearlyPrice: number;
}

interface BreakdownItem {
  label: string;
  seats: number;
  pricePerSeat: number;
  total: number;
}

interface TeamPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TeamPlanDialog({ open, onOpenChange }: TeamPlanDialogProps) {
  const [userCount, setUserCount] = useState(3);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const { startCheckout, loading } = usePolarCheckout();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleContinueToBilling = () => {
    if (!user) {
      navigate('/auth/signup');
      return;
    }
    startCheckout({
      productId: POLAR_PRODUCTS.team,
      plan: 'team',
      billingCycle,
      seats: userCount,
    });
  };

  const MIN_USERS = 3;
  const BASE_PRICE = 55;

  const pricingTiers: PricingTier[] = [
  { min: 1, max: 9, monthlyPrice: 55, yearlyPrice: 35 },
  { min: 10, max: 49, monthlyPrice: 44, yearlyPrice: 27 },
  { min: 50, max: Infinity, monthlyPrice: 33, yearlyPrice: 19 }];


  const calculateBreakdown = (): BreakdownItem[] => {
    const breakdown: BreakdownItem[] = [];
    let remainingSeats = userCount;
    let seatIndex = 1;

    for (const tier of pricingTiers) {
      if (remainingSeats <= 0) break;

      const tierStart = Math.max(seatIndex, tier.min);
      const tierEnd = Math.min(seatIndex + remainingSeats - 1, tier.max);
      const seatsInTier = tierEnd - tierStart + 1;

      if (seatsInTier > 0 && seatIndex <= tier.max) {
        const pricePerSeat = billingCycle === 'monthly' ? tier.monthlyPrice : tier.yearlyPrice;
        const total = seatsInTier * pricePerSeat;

        const endLabel = tier.max === Infinity ? userCount : Math.min(tierEnd, tier.max);
        const label = billingCycle === 'yearly' ?
        `Seats ${tierStart}-${endLabel}: SR${pricePerSeat}/seat * 12 mos × ${seatsInTier}` :
        `Seats ${tierStart}-${endLabel}: SR${pricePerSeat}/month × ${seatsInTier}`;

        breakdown.push({
          label,
          seats: seatsInTier,
          pricePerSeat,
          total: billingCycle === 'yearly' ? total * 12 : total
        });

        remainingSeats -= seatsInTier;
        seatIndex = tierEnd + 1;
      }
    }

    return breakdown;
  };

  const calculateTotal = () => {
    const breakdown = calculateBreakdown();
    return breakdown.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateBaseTotal = () => {
    return billingCycle === 'yearly' ? userCount * BASE_PRICE * 12 : userCount * BASE_PRICE;
  };

  const calculateSavings = () => {
    return calculateBaseTotal() - calculateTotal();
  };

  const getDisplayYearlyPrice = () => {
    if (userCount >= 50) return 19;
    if (userCount >= 10) return 27;
    return 35;
  };

  const incrementUsers = () => setUserCount((prev) => prev + 1);
  const decrementUsers = () =>
  setUserCount((prev) => Math.max(MIN_USERS, prev - 1));

  const handleUserInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value) || MIN_USERS;
    setUserCount(Math.max(MIN_USERS, value));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-start justify-between">
            <div className="space-y-3 flex-1 pr-8">
              <DialogTitle className="text-xl font-semibold">
                Select your team plan
              </DialogTitle>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Save 55–75% with yearly billing: SR35/user (Seats 3–9), SR27/user
                (10–49), SR19/user (50+). Monthly plans start at SR55/user. Tap
                the info icon for the full discount breakdown.{' '}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="inline h-4 w-4 cursor-help text-muted-foreground hover:text-foreground transition-colors" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="w-80 p-4">
                      <div className="space-y-3 text-xs">
                        <div>
                          <div className="mb-1.5 font-semibold">
                            Yearly (best value – compared to Pro Monthly):
                          </div>
                          <div className="space-y-1 text-muted-foreground">
                            <div>• 1-9 seats: SR35/user/mo (55% off)</div>
                            <div>• 10-49 seats: SR27/user/mo (65% off)</div>
                            <div>• 50+ seats: SR19/user/mo (75% off)</div>
                          </div>
                        </div>
                        <div>
                          <div className="mb-1.5 font-semibold">
                            Monthly (compared to Pro Monthly):
                          </div>
                          <div className="space-y-1 text-muted-foreground">
                            <div>• 1-9 seats: SR55/user/mo (25% off)</div>
                            <div>• 10-49 seats: SR44/user/mo (40% off)</div>
                            <div>• 50+ seats: SR33/user/mo (55% off)</div>
                          </div>
                        </div>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </p>
            </div>
          </div>
        </DialogHeader>

        {/* Main Content */}
        <div className="px-6 pb-6">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column - Plan Selection */}
            <div className="flex-1">
              {/* User Count Input */}
              <div className="mb-6">
                <div className="mb-2 text-sm font-medium">
                  Users{' '}
                  <span className="text-sm font-normal text-muted-foreground">
                    (Minimum 3 seats required)
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={userCount}
                    onChange={handleUserInput}
                    min={MIN_USERS}
                    className="h-12 w-full rounded-lg border border-input bg-background px-4 text-base font-normal focus:outline-none focus:ring-2 focus:ring-ring [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />

                  <Button
                    variant="outline"
                    size="icon"
                    disabled={userCount <= MIN_USERS}
                    onClick={decrementUsers}
                    className="h-12 w-12 flex-shrink-0">

                    <Minus className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={incrementUsers}
                    className="h-12 w-12 flex-shrink-0">

                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Plan Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Monthly Plan */}
                <button
                  type="button"
                  onClick={() => setBillingCycle('monthly')}
                  className={`flex flex-col text-left rounded-2xl border-2 bg-card p-6 transition-all hover:shadow-sm ${
                  billingCycle === 'monthly' ?
                  'border-foreground shadow-sm' :
                  'border-border'}`
                  }>

                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-medium">Monthly</h3>
                    <div
                      className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
                      billingCycle === 'monthly' ?
                      'border-foreground bg-foreground' :
                      'border-muted-foreground/40'}`
                      }>

                      {billingCycle === 'monthly' &&
                      <Check className="h-3.5 w-3.5 text-background" />
                      }
                    </div>
                  </div>
                  <div className="mb-6">
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-semibold">
                        SR{BASE_PRICE}
                      </span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      per user/month
                    </div>
                  </div>
                  <ul className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-start">
                      <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                      Billed monthly
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                      Minimum 3 users
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                      Add or remove users as needed
                    </li>
                  </ul>
                </button>

                {/* Yearly Plan */}
                <div className="relative">
                  <div className="absolute -top-3 inset-x-0 flex justify-center z-10">
                    <span className="inline-flex items-center rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-md">
                      Save 40%+
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`flex flex-col text-left h-full w-full rounded-2xl border-2 bg-card p-6 transition-all hover:shadow-sm ${
                    billingCycle === 'yearly' ?
                    'border-foreground shadow-sm' :
                    'border-border'}`
                    }>

                    <div className="mb-4 flex items-center justify-between">
                      <h3 className="text-lg font-medium">Yearly</h3>
                      <div
                        className={`flex h-6 w-6 items-center justify-center rounded-full border-2 transition-colors ${
                        billingCycle === 'yearly' ?
                        'border-foreground bg-foreground' :
                        'border-muted-foreground/40'}`
                        }>

                        {billingCycle === 'yearly' &&
                        <Check className="h-3.5 w-3.5 text-background" />
                        }
                      </div>
                    </div>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-semibold">
                          SR{getDisplayYearlyPrice()}
                        </span>
                        <span className="text-base text-muted-foreground line-through">
                          SR{BASE_PRICE}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        per user/month
                      </div>
                    </div>
                    <ul className="space-y-3 text-sm text-muted-foreground">
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                        Billed Yearly
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                        Minimum 3 users
                      </li>
                      <li className="flex items-start">
                        <span className="mr-2 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
                        Add and reassign users as needed
                      </li>
                    </ul>
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column - Summary */}
            <div className="w-full lg:w-[340px]">
              <div className="flex h-full flex-col rounded-2xl border bg-secondary/50 p-5">
                <h3 className="mb-4 text-lg font-semibold">Summary</h3>

                <div className="mb-auto space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-start">
                      <div className="text-sm font-medium">
                        Team Plan - {userCount} Seats (
                        {billingCycle === 'monthly' ? 'Monthly' : 'Yearly'})
                      </div>
                      <div className="text-sm font-medium">
                        SR{calculateTotal().toLocaleString()}
                      </div>
                    </div>
                    {calculateBreakdown().map((item, index) =>
                    <div key={index} className="ml-4 flex justify-between text-xs text-muted-foreground">
                        <span>{item.label}</span>
                        <span>SR{item.total.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-8">
                  <div className="mb-6 border-t pt-5">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="text-sm font-medium">Today's total</div>
                      <span className="text-lg font-semibold">
                        SR{calculateTotal().toLocaleString()}
                      </span>
                    </div>
                    {calculateSavings() > 0 &&
                    <div className="mt-2 text-xs text-muted-foreground">
                        You saved SR{calculateSavings().toLocaleString()} vs. base pricing (SR{calculateBaseTotal().toLocaleString()})
                      </div>
                    }
                    <div className="mt-1 text-xs text-muted-foreground">
                      Billed {billingCycle === 'monthly' ? 'monthly' : 'annually'}{' '}
                      starting today
                    </div>
                  </div>
                  <Button
                    className="w-full h-12 text-base font-medium"
                    onClick={handleContinueToBilling}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Continue to billing'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>);

}